import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAllPublicAndPrivateSettings, IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { ExpiringClaims, Permission, Resource } from '@pepperhq/auth-client';
import { setCurrencySymbol } from 'store/manifest/manifestSlice';
import { EPointOfSaleProvider } from 'components/settings/PointOfSaleSettings';
import { IComputedSettings } from 'components/settings/model';
import type { AppDispatch } from '../store';

export interface SettingsState {
    settings: IPublicAndPrivateSettings;
    computedSettings: IComputedSettings;
    loading: boolean;
    error?: string;
}

export const initialState: SettingsState = {
    settings: {},
    computedSettings: {},
    loading: true,
    error: undefined
};

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        startRequest(state) {
            state.loading = true;
        },
        endRequest(state) {
            state.loading = false;
        },
        loadSuccess(
            state,
            action: PayloadAction<{
                settings: IPublicAndPrivateSettings;
                computedSettings: IComputedSettings;
            }>
        ) {
            state.error = undefined;
            state.settings = action.payload.settings;
            state.computedSettings = action.payload.computedSettings;
        },
        loadError(state, action: PayloadAction<string>) {
            state.loading = false;
            state.error = action.payload;
        },
        reset() {
            return { ...initialState };
        }
    }
});

export const { startRequest, endRequest, loadSuccess, loadError, reset } = settingsSlice.actions;

export function resetPublicSettings() {
    return function (dispatch: AppDispatch) {
        dispatch(reset());
    };
}

export function getPublicAndPrivateSettings(claims: ExpiringClaims) {
    return async function (dispatch: AppDispatch) {
        dispatch(startRequest());
        try {
            let settings: DeepPartial<IAllPublicAndPrivateSettings>;
            if (claims.hasPermission(Resource.Secret, Permission.read)) {
                settings = await new LocationSettingsApi().getPublicAndPrivateSettings(null);
            } else if (claims.hasPermission(Resource.Setting, Permission.read)) {
                settings = await new LocationSettingsApi().getPublicSettings(null);
            }
            const computedSettings: IComputedSettings = {
                dineInEnabled: settings?.posProvider === EPointOfSaleProvider.QIKSERVE,
                // Delivery provider might be different, check with platform before the prod deploy
                deliveryEnabled: settings?.deliveryProvider === 'UBER_DIRECT'
            };
            dispatch(setCurrencySymbol(settings?.region?.currencySymbol));
            dispatch(loadSuccess({ computedSettings, settings }));
        } catch (e) {
            dispatch(loadError(e.message));
        } finally {
            dispatch(endRequest());
        }
    };
}

export default settingsSlice.reducer;
